import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Search, Plus, User, Car, Fuel, Key, Camera,
  Trash2, ShieldAlert, PenLine, CheckCircle2, WifiOff, Wifi, X, ScanLine, Loader2,
} from "lucide-react";
import { CRM_API, errMsg } from "./api";
import { useCrmAuth } from "./CrmAuth";
import { compressImage, enqueuePhoto, drainQueue, listQueue } from "./photoQueue";
import CarDiagram from "./components/CarDiagram";
import SignaturePad from "./components/SignaturePad";
import PageHeader from "./components/PageHeader";
import SearchSelect from "./components/SearchSelect";
import WarningLightsPicker from "./components/WarningLightsPicker";
import { findDuplicateByPhone } from "./components/QuickPick";

const STEPS = ["Customer", "Vehicle", "Info", "Photos", "Damage", "Review", "Sign"];
const REQUIRED_PHOTOS = [
  { kind: "front", label: "Front" },
  { kind: "front_left", label: "Front Left" },
  { kind: "driver_side", label: "Driver Side" },
  { kind: "rear_left", label: "Rear Left" },
  { kind: "rear", label: "Rear" },
  { kind: "rear_right", label: "Rear Right" },
  { kind: "passenger_side", label: "Passenger Side" },
  { kind: "front_right", label: "Front Right" },
  { kind: "windshield", label: "Windshield" },
  { kind: "interior", label: "Interior" },
  { kind: "wheels", label: "Wheels" },
];
const DAMAGE_TYPES = [
  { key: "scratch", label: "Scratch" }, { key: "dent", label: "Dent" },
  { key: "chip", label: "Chip" }, { key: "crack", label: "Crack" },
  { key: "paint", label: "Paint" }, { key: "wheel", label: "Wheel" },
  { key: "glass", label: "Glass" }, { key: "interior", label: "Interior" },
  { key: "other", label: "Other" },
];
const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] transition-colors text-white text-[15px]";
const SEVC = { minor: "#38bdf8", moderate: "#f59e0b", major: "#ef4444" };

export default function CheckIn() {
  const { user } = useCrmAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [info, setInfo] = useState({ mileage: "", fuel_level: "half", warning_lights: "", keys_received: 1, valuables: "", existing_ppf: false, existing_tint: false, previous_repairs: "", services_requested: [] });
  const [photos, setPhotos] = useState([]); // uploaded
  const [pending, setPending] = useState([]); // queued
  const [markers, setMarkers] = useState([]);
  const [markerBuf, setMarkerBuf] = useState(null); // { x, y, view } waiting for type/severity
  const [signOpen, setSignOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  // Online listener + queue drain
  useEffect(() => {
    const on = () => { setOnline(true); if (inspection?.id) drainQueue(inspection.id, BACKEND, (p) => setPhotos((prev) => [...prev, p])).then(refreshQueue); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    // eslint-disable-next-line
  }, [inspection?.id]);

  const refreshQueue = async () => {
    if (!inspection?.id) return;
    setPending(await listQueue(inspection.id));
  };

  // ---- Step 0: Customer picker ----
  const [custQuery, setCustQuery] = useState("");
  const [custRes, setCustRes] = useState([]);
  const [newCustForm, setNewCustForm] = useState(null);
  useEffect(() => {
    if (step !== 0) return;
    const t = setTimeout(() => {
      CRM_API.get(`/customers${custQuery ? `?q=${encodeURIComponent(custQuery)}` : ""}`).then(({ data }) => setCustRes(data));
    }, 180);
    return () => clearTimeout(t);
  }, [custQuery, step]);

  const createCustomer = async () => {
    if (!newCustForm?.first_name || !newCustForm?.phone) { toast.error("Name and phone required"); return; }
    try {
      const dup = await findDuplicateByPhone(newCustForm.phone);
      if (dup) {
        setCustomer(dup);
        setNewCustForm(null);
        toast.warning(`Customer with this phone already exists: ${dup.first_name} ${dup.last_name} — existing profile selected`);
        setStep(1);
        return;
      }
      const c = newCustForm;
      const { data } = await CRM_API.post("/customers", {
        first_name: (c.first_name || "").trim(),
        last_name: (c.last_name || "").trim(),
        phone: (c.phone || "").trim(),
        email: (c.email || "").trim() || null,   // empty string fails EmailStr → 422
        address: (c.address || "").trim() || null,
        marketing_consent: !!c.marketing_consent,
      });
      setCustomer(data);
      setNewCustForm(null);
      toast.success("Customer created");
      setStep(1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Step 1: Vehicle picker/create ----
  const [vehicles, setVehicles] = useState([]);
  const [newVehForm, setNewVehForm] = useState(null);
  const [vinBusy, setVinBusy] = useState(false);
  // Vehicle catalog (same public catalog the price calculator uses)
  const [vehYears, setVehYears] = useState([]);
  const [vehMakes, setVehMakes] = useState([]);
  const [vehModels, setVehModels] = useState([]);
  const [vehCatBusy, setVehCatBusy] = useState(false);
  useEffect(() => {
    fetch(`${BACKEND}/api/vehicles/years`).then((r) => r.json())
      .then((d) => setVehYears(Array.isArray(d) ? d.map(String) : [])).catch(() => {});
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (!newVehForm?.year) { setVehMakes([]); return; }
    setVehCatBusy(true);
    fetch(`${BACKEND}/api/vehicles/makes?year=${encodeURIComponent(newVehForm.year)}`).then((r) => r.json())
      .then((d) => setVehMakes(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setVehCatBusy(false));
    // eslint-disable-next-line
  }, [newVehForm?.year]);
  useEffect(() => {
    if (!newVehForm?.year || !newVehForm?.make) { setVehModels([]); return; }
    setVehCatBusy(true);
    fetch(`${BACKEND}/api/vehicles/models?year=${encodeURIComponent(newVehForm.year)}&make=${encodeURIComponent(newVehForm.make)}`)
      .then((r) => r.json()).then((d) => setVehModels(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setVehCatBusy(false));
    // eslint-disable-next-line
  }, [newVehForm?.year, newVehForm?.make]);

  // Quick Check-In deep link: /crm/check-in?customer=<id>&vehicle=<id>
  // (e.g. opened from a Calendar appointment). Preselects both and lands on
  // the Vehicle step — one tap on Next creates the inspection as usual.
  useEffect(() => {
    const cid = searchParams.get("customer");
    const vid = searchParams.get("vehicle");
    if (!cid || customer) return;
    (async () => {
      try {
        // GET /customers/{id} returns { customer, vehicles, ... } wrapper
        const { data } = await CRM_API.get(`/customers/${cid}`);
        const c = data.customer || data;
        setCustomer(c);
        if (vid) {
          const v = (data.vehicles || []).find((x) => x.id === vid);
          if (v) setVehicle(v);
        }
        setStep(1);
      } catch {
        toast.error("Customer not found for quick check-in");
      }
    })();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (!customer) return;
    CRM_API.get(`/vehicles?customer_id=${customer.id}`).then(({ data }) => setVehicles(data));
  }, [customer]);

  const decodeVin = async () => {
    const vin = (newVehForm?.vin || "").trim().toUpperCase();
    if (vin.length < 11) { toast.error("VIN too short"); return; }
    setVinBusy(true);
    try {
      const { data } = await CRM_API.post("/vin/decode", { vin });
      setNewVehForm((f) => ({
        ...f, vin, year: data.year || f.year, make: data.make || f.make,
        model: data.model || f.model, trim: data.trim || f.trim,
      }));
      toast.success(`${data.year || "?"} ${data.make || ""} ${data.model || ""}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setVinBusy(false); }
  };

  const createVehicle = async () => {
    try {
      const payload = {
        customer_id: customer.id,
        vin: newVehForm.vin || null, license_plate: newVehForm.license_plate || null,
        year: newVehForm.year ? Number(newVehForm.year) : null,
        make: newVehForm.make || null, model: newVehForm.model || null,
        trim: newVehForm.trim || null, color: newVehForm.color || null,
        mileage: newVehForm.mileage ? Number(newVehForm.mileage) : null,
      };
      const { data } = await CRM_API.post("/vehicles", payload);
      setVehicle(data); setNewVehForm(null);
      toast.success("Vehicle added");
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Advance from vehicle: create inspection ----
  const startInspection = async () => {
    if (!customer || !vehicle) return;
    try {
      const { data } = await CRM_API.post("/inspections", { customer_id: customer.id, vehicle_id: vehicle.id });
      setInspection(data);
      setStep(2);
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Step 2: Vehicle info save ----
  const saveInfo = async () => {
    if (!inspection) return;
    setBusy(true);
    try {
      const payload = {
        mileage: info.mileage ? Number(info.mileage) : null,
        fuel_level: info.fuel_level || null,
        warning_lights: info.warning_lights || null,
        keys_received: Number(info.keys_received) || 1,
        valuables: info.valuables || null,
        existing_ppf: !!info.existing_ppf, existing_tint: !!info.existing_tint,
        previous_repairs: info.previous_repairs || null,
        services_requested: info.services_requested || [],
      };
      await CRM_API.patch(`/inspections/${inspection.id}/info`, payload);
      setStep(3);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  // ---- Step 3: Photos ----
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [currentKind, setCurrentKind] = useState(null);

  // Diagnostics: report the exact photo-upload failure reason (legacy iPad debugging)
  const reportPhotoError = (stage, err) => {
    try {
      const msg = String((err && (err.message || err.statusText || err)) || "unknown");
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/analytics/event", JSON.stringify({
          event: "client_js_error",
          data: { msg: `photo:${stage}: ${msg}`.slice(0, 150), src: "checkin", line: "0", path: location.pathname.slice(0, 60) },
          page: location.pathname,
        }));
      }
    } catch (_) { /* diagnostics must never break the flow */ }
  };

  const capturePhoto = async (files, kind) => {
    if (!files?.length || !inspection) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const blob = await compressImage(file);
        if (online) {
          try {
            const fd = new FormData();
            fd.append("kind", kind || "extra");
            fd.append("file", blob, `${kind || "extra"}.jpg`);
            const res = await fetch(`${BACKEND}/api/crm/inspections/${inspection.id}/photos`, { method: "POST", body: fd, credentials: "include" });
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${(await res.text()).slice(0, 100)}`);
            const photo = await res.json();
            setPhotos((prev) => [...prev, photo]);
          } catch (e) {
            reportPhotoError("upload", e);
            await enqueuePhoto({ inspectionId: inspection.id, kind: kind || "extra", blob, mime: blob.type });
            toast.warning("Upload failed — saved to offline queue, will retry");
          }
        } else {
          await enqueuePhoto({ inspectionId: inspection.id, kind: kind || "extra", blob, mime: blob.type });
          toast.info("Photo saved offline — will sync when online");
        }
      }
      await refreshQueue();
    } catch (e) { reportPhotoError("capture", e); toast.error(errMsg(e)); }
    finally { setUploading(false); setCurrentKind(null); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const removePhoto = async (p) => {
    try {
      await CRM_API.delete(`/inspections/${inspection.id}/photos/${p.id}`);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const photosByKind = useMemo(() => {
    const m = {};
    for (const p of photos) { m[p.kind] = m[p.kind] || []; m[p.kind].push(p); }
    return m;
  }, [photos]);
  const missingPhotos = REQUIRED_PHOTOS.filter((r) => !photosByKind[r.kind]?.length);
  const KIND_LABEL = useMemo(() => {
    const m = { extra: "Extra" };
    REQUIRED_PHOTOS.forEach((r) => { m[r.kind] = r.label; });
    return m;
  }, []);
  // Photos not shown as a tile's primary image: extras + 2nd+ photos of any section
  const primaryPhotoIds = useMemo(
    () => new Set(REQUIRED_PHOTOS.map((r) => photosByKind[r.kind]?.[0]?.id).filter(Boolean)),
    [photosByKind]
  );
  const additionalPhotos = photos.filter((p) => !primaryPhotoIds.has(p.id));
  const [extraKind, setExtraKind] = useState("extra");

  // ---- Step 4: Damage ----
  const [damageView, setDamageView] = useState("top");
  const [markerDetail, setMarkerDetail] = useState(null); // existing marker being viewed
  const addMarker = async (payload) => {
    setMarkerBuf({ ...payload });
  };
  const commitMarker = async ({ type, severity, description, photoFile }) => {
    try {
      const { data } = await CRM_API.post(`/inspections/${inspection.id}/markers`, { ...markerBuf, type, severity, description: description || "" });
      let marker = data;
      if (photoFile) {
        try {
          const blob = await compressImage(photoFile);
          const fd = new FormData();
          fd.append("file", blob, "damage.jpg");
          fd.append("kind", "extra");
          fd.append("marker_id", marker.id);
          const { data: photo } = await CRM_API.post(`/inspections/${inspection.id}/photos`, fd);
          marker = { ...marker, photo_ids: [...(marker.photo_ids || []), photo.id] };
        } catch (e) {
          reportPhotoError("marker-photo", e);
          toast.error("Marker saved, but photo upload failed: " + errMsg(e));
        }
      }
      setMarkers((m) => [...m, marker]);
      setMarkerBuf(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const removeMarker = async (m) => {
    try { await CRM_API.delete(`/inspections/${inspection.id}/markers/${m.id}`); setMarkers((all) => all.filter((x) => x.id !== m.id)); }
    catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Step 5: Sign & complete ----
  const [signerName, setSignerName] = useState("");
  const finalize = async (dataUrl) => {
    const name = signerName.trim() || `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim();
    if (!name) { toast.error("Customer name is required"); return; }
    try {
      const { data } = await CRM_API.post(`/inspections/${inspection.id}/signature`, { data_url: dataUrl, customer_name: name });
      toast.success("Check-in complete");
      // Auto-create Work Order
      try {
        const wo = await CRM_API.post("/work-orders", {
          customer_id: customer.id, vehicle_id: vehicle.id, inspection_id: inspection.id,
          service_ids: info.services_requested || [],
        });
        toast.success(`Work Order ${wo.data.wo_number} created`);
      } catch { /* ignore */ }
      // Land on the full check-in report (work order is linked from there)
      nav(`/crm/inspections/${inspection.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canNext = () => {
    if (step === 0) return !!customer;
    if (step === 1) return !!vehicle;
    if (step === 2) return true; // saveInfo will be called
    if (step === 3) return missingPhotos.length === 0 || online === false;
    if (step === 4) return true;
    if (step === 5) return true; // review is read-only
    return false;
  };

  const next = async () => {
    if (step === 1 && !inspection) { await startInspection(); return; }
    if (step === 2) { await saveInfo(); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div data-testid="crm-checkin" className="pb-32">
      <PageHeader
        eyebrow={`Step ${step + 1} of ${STEPS.length}`}
        title="Vehicle Check-In"
        subtitle="Fast, thorough, insurance-grade documentation."
        right={
          <div className="flex items-center gap-3 text-xs">
            {online ? <span className="flex items-center gap-1.5 text-emerald-400"><Wifi className="w-3.5 h-3.5" /> Online</span>
                    : <span className="flex items-center gap-1.5 text-amber-400"><WifiOff className="w-3.5 h-3.5" /> Offline</span>}
            {pending.length > 0 && <span className="text-amber-400" data-testid="pending-count">{pending.length} queued</span>}
          </div>
        }
      />
      {/* Stepper */}
      <div className="px-6 md:px-10 pt-6 flex items-center gap-2 overflow-x-auto no-scrollbar">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 whitespace-nowrap ${i < step ? "text-emerald-400" : i === step ? "text-white" : "text-white/30"}`} data-testid={`step-${i}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono-alt ${i < step ? "bg-emerald-500/20" : i === step ? "bg-[#1F5EFF]" : "bg-white/8"}`}>{i < step ? "✓" : i + 1}</span>
            <span className="text-sm">{s}</span>
            {i < STEPS.length - 1 && <span className="text-white/15 mx-2">/</span>}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="px-6 md:px-10 py-6 max-w-5xl">
        {/* STEP 0: CUSTOMER */}
        {step === 0 && (
          <div data-testid="step-customer">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg">
                <Search className="w-4 h-4 text-white/40" />
                <input value={custQuery} onChange={(e) => setCustQuery(e.target.value)} placeholder="Phone, name or email…"
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder-white/30" autoFocus data-testid="ci-customer-search" />
              </div>
              <button onClick={() => setNewCustForm({ first_name: "", last_name: "", phone: "", email: "", address: "" })} data-testid="ci-new-customer-btn"
                className="px-4 py-2.5 rounded-lg bg-[#1F5EFF] text-white text-sm font-medium hover:bg-white hover:text-black transition-colors">
                <Plus className="w-4 h-4 inline mr-1" />New
              </button>
            </div>
            {newCustForm && (
              <div className="mb-4 p-4 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25 space-y-3" data-testid="ci-new-customer-form">
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="First name *" value={newCustForm.first_name} onChange={(e) => setNewCustForm((f) => ({ ...f, first_name: e.target.value }))} className={inp} data-testid="ci-nc-first" />
                  <input placeholder="Last name" value={newCustForm.last_name} onChange={(e) => setNewCustForm((f) => ({ ...f, last_name: e.target.value }))} className={inp} data-testid="ci-nc-last" />
                  <input placeholder="Phone *" value={newCustForm.phone} onChange={(e) => setNewCustForm((f) => ({ ...f, phone: e.target.value }))} className={inp} inputMode="tel" data-testid="ci-nc-phone" />
                  <input placeholder="Email" value={newCustForm.email} onChange={(e) => setNewCustForm((f) => ({ ...f, email: e.target.value }))} className={inp} inputMode="email" data-testid="ci-nc-email" />
                </div>
                <input placeholder="Address" value={newCustForm.address} onChange={(e) => setNewCustForm((f) => ({ ...f, address: e.target.value }))} className={inp} data-testid="ci-nc-address" />
                <div className="flex gap-2">
                  <button onClick={createCustomer} className="px-5 py-2.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium" data-testid="ci-nc-save">Save & continue</button>
                  <button onClick={() => setNewCustForm(null)} className="px-4 py-2.5 text-white/50 hover:text-white text-sm">Cancel</button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {custRes.map((c) => (
                <button key={c.id} onClick={() => { setCustomer(c); setStep(1); }} data-testid={`ci-cust-${c.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${customer?.id === c.id ? "border-[#1F5EFF] bg-[#1F5EFF]/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
                  <User className="w-4 h-4 text-white/40" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-white/40 text-xs truncate">{c.phone} · {c.email || "—"}</div>
                  </div>
                  <span className="text-[10px] font-mono-alt text-white/40 uppercase">{c.vehicles_count} veh</span>
                </button>
              ))}
              {!custRes.length && !newCustForm && <div className="p-8 text-center text-white/30 text-sm">Search or add a new customer above.</div>}
            </div>
          </div>
        )}

        {/* STEP 1: VEHICLE */}
        {step === 1 && customer && (
          <div data-testid="step-vehicle">
            <div className="mb-4 text-white/60 text-sm">Customer · <span className="text-white">{customer.first_name} {customer.last_name}</span></div>
            {!newVehForm && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {vehicles.map((v) => (
                    <button key={v.id} onClick={() => setVehicle(v)} data-testid={`ci-veh-${v.id}`}
                      className={`p-4 rounded-xl border text-left transition-colors ${vehicle?.id === v.id ? "border-[#1F5EFF] bg-[#1F5EFF]/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Car className="w-4 h-4 text-[#1F5EFF]" />
                        <div className="text-white text-sm font-medium">{[v.year, v.make, v.model].filter(Boolean).join(" ")}</div>
                      </div>
                      <div className="text-white/40 text-xs font-mono-alt">{[v.vin, v.license_plate].filter(Boolean).join(" · ") || "—"}</div>
                    </button>
                  ))}
                </div>
                <button onClick={() => setNewVehForm({ vin: "", license_plate: "", year: "", make: "", model: "", trim: "", color: "", mileage: "" })} data-testid="ci-new-vehicle-btn"
                  className="px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium">
                  <Plus className="w-4 h-4 inline mr-1" />Add new vehicle
                </button>
              </>
            )}
            {newVehForm && (
              <div className="p-4 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25 space-y-3" data-testid="ci-new-vehicle-form">
                <div className="flex gap-2">
                  <input placeholder="VIN (17 chars)" value={newVehForm.vin} onChange={(e) => setNewVehForm((f) => ({ ...f, vin: e.target.value.toUpperCase() }))} maxLength={17}
                    className={inp + " font-mono-alt tracking-wider"} data-testid="ci-nv-vin" />
                  <button type="button" onClick={decodeVin} disabled={vinBusy}
                    className="px-4 rounded-lg bg-[#1F5EFF] text-white text-sm font-medium hover:bg-white hover:text-black disabled:opacity-50" data-testid="ci-nv-decode">
                    {vinBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 inline mr-1" />Decode</>}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SearchSelect value={newVehForm.year} placeholder="Year" testid="ci-nv-year"
                    options={vehYears} allowCustom
                    onChange={(y) => setNewVehForm((f) => ({ ...f, year: y, make: "", model: "" }))} />
                  <SearchSelect value={newVehForm.make} placeholder="Make" testid="ci-nv-make"
                    options={vehMakes} allowCustom disabled={!newVehForm.year} loading={vehCatBusy && !!newVehForm.year && !vehMakes.length}
                    onChange={(m) => setNewVehForm((f) => ({ ...f, make: m, model: "" }))} />
                  <SearchSelect value={newVehForm.model} placeholder="Model" testid="ci-nv-model"
                    options={vehModels} allowCustom disabled={!newVehForm.make} loading={vehCatBusy && !!newVehForm.make && !vehModels.length}
                    onChange={(m) => setNewVehForm((f) => ({ ...f, model: m }))} />
                  <input placeholder="Trim" value={newVehForm.trim} onChange={(e) => setNewVehForm((f) => ({ ...f, trim: e.target.value }))} className={inp} />
                  <input placeholder="Color" value={newVehForm.color} onChange={(e) => setNewVehForm((f) => ({ ...f, color: e.target.value }))} className={inp} />
                  <input placeholder="Plate" value={newVehForm.license_plate} onChange={(e) => setNewVehForm((f) => ({ ...f, license_plate: e.target.value.toUpperCase() }))} className={inp + " uppercase"} />
                </div>
                <div className="flex gap-2">
                  <button onClick={createVehicle} className="px-5 py-2.5 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white text-sm font-medium" data-testid="ci-nv-save">Save vehicle</button>
                  <button onClick={() => setNewVehForm(null)} className="px-4 py-2.5 text-white/50 hover:text-white text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: INFO */}
        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="step-info">
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">MILEAGE (KM)</div>
              <input type="number" inputMode="numeric" value={info.mileage} onChange={(e) => setInfo({ ...info, mileage: e.target.value })} className={inp} data-testid="ci-info-mileage" />
            </label>
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">FUEL LEVEL</div>
              <select value={info.fuel_level} onChange={(e) => setInfo({ ...info, fuel_level: e.target.value })} className={inp} data-testid="ci-info-fuel">
                <option value="empty">Empty</option>
                <option value="quarter">1/4</option>
                <option value="half">1/2</option>
                <option value="three_quarter">3/4</option>
                <option value="full">Full</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">KEYS RECEIVED</div>
              <input type="number" min="0" max="10" value={info.keys_received} onChange={(e) => setInfo({ ...info, keys_received: e.target.value })} className={inp} data-testid="ci-info-keys" />
            </label>
            <div className="block">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">WARNING LIGHTS</div>
              <WarningLightsPicker value={info.warning_lights} onChange={(v) => setInfo({ ...info, warning_lights: v })} />
            </div>
            <label className="block md:col-span-2">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">VALUABLES INSIDE</div>
              <input value={info.valuables} onChange={(e) => setInfo({ ...info, valuables: e.target.value })} placeholder="Sunglasses, coins, dashcam…" className={inp} data-testid="ci-info-valuables" />
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer">
              <input type="checkbox" checked={info.existing_ppf} onChange={(e) => setInfo({ ...info, existing_ppf: e.target.checked })} className="w-4 h-4 accent-[#1F5EFF]" data-testid="ci-info-ppf" />
              <span className="text-sm text-white/80">Existing PPF on vehicle</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer">
              <input type="checkbox" checked={info.existing_tint} onChange={(e) => setInfo({ ...info, existing_tint: e.target.checked })} className="w-4 h-4 accent-[#1F5EFF]" data-testid="ci-info-tint" />
              <span className="text-sm text-white/80">Existing window tint</span>
            </label>
            <label className="block md:col-span-2">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">PREVIOUS PAINT/BODY REPAIRS</div>
              <textarea rows={3} value={info.previous_repairs} onChange={(e) => setInfo({ ...info, previous_repairs: e.target.value })} className={inp + " resize-y"} data-testid="ci-info-repairs" />
            </label>
          </div>
        )}

        {/* STEP 3: PHOTOS */}
        {step === 3 && inspection && (
          <div data-testid="step-photos">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {REQUIRED_PHOTOS.map((r) => {
                const done = photosByKind[r.kind]?.length > 0;
                return (
                  <label key={r.kind} className={`relative aspect-[4/3] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-center cursor-pointer transition-colors ${done ? "border-emerald-500/60 bg-emerald-500/8" : "border-white/15 bg-white/3 hover:border-[#1F5EFF] hover:bg-[#1F5EFF]/8"}`} data-testid={`photo-slot-${r.kind}`}>
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; capturePhoto(files, r.kind); }} />
                    {done ? (
                      <>
                        <img src={`${BACKEND}/api/crm/inspections/${inspection.id}/photos/${photosByKind[r.kind][0].id}/file`}
                          alt={r.label} className="absolute inset-0 w-full h-full object-cover rounded-xl" />
                        <div className="absolute inset-0 bg-black/40 hover:bg-black/60 rounded-xl transition-colors flex flex-col items-center justify-center opacity-0 hover:opacity-100">
                          <button type="button" onClick={(e) => { e.preventDefault(); removePhoto(photosByKind[r.kind][0]); }}
                            className="p-2 rounded-full bg-red-500/80 hover:bg-red-500 text-white" data-testid={`photo-remove-${r.kind}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="absolute bottom-2 left-2 right-2 font-mono-alt text-[10px] text-white uppercase tracking-widest z-10">{r.label} ✓</div>
                      </>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-white/40" />
                        <div className="font-mono-alt text-[10px] text-white/60 uppercase tracking-widest">{r.label}</div>
                      </>
                    )}
                  </label>
                );
              })}
              {/* Add photo to a chosen section */}
              <div className="aspect-[4/3] rounded-xl border-2 border-dashed border-[#1F5EFF]/40 bg-[#1F5EFF]/5 flex flex-col p-2 gap-1.5" data-testid="photo-slot-custom">
                <select
                  value={extraKind}
                  onChange={(e) => setExtraKind(e.target.value)}
                  className="bg-[#101318] border border-white/15 rounded-md px-2 py-1.5 text-[11px] text-white outline-none focus:border-[#1F5EFF]"
                  data-testid="photo-kind-select"
                >
                  <option value="extra">Extra / other</option>
                  {REQUIRED_PHOTOS.map((r) => (
                    <option key={r.kind} value={r.kind}>{r.label}</option>
                  ))}
                </select>
                <label className="flex-1 w-full flex flex-col items-center justify-center gap-1 cursor-pointer rounded-lg hover:bg-[#1F5EFF]/10 transition-colors">
                  <input type="file" accept="image/*" capture="environment" multiple className="hidden"
                    onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ""; capturePhoto(files, extraKind); }} />
                  <Plus className="w-7 h-7 text-[#1F5EFF]" />
                  <div className="font-mono-alt text-[9px] text-[#8db2ff] uppercase tracking-widest text-center">
                    Add photo<br />+{additionalPhotos.length}
                  </div>
                </label>
              </div>
            </div>
            {additionalPhotos.length > 0 && (
              <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="additional-photos">
                {additionalPhotos.map((p) => (
                  <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group">
                    <img src={`${BACKEND}/api/crm/inspections/${inspection.id}/photos/${p.id}/file`} alt="" className="w-full h-full object-cover" />
                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 font-mono-alt text-[8px] uppercase tracking-wider text-white/80">
                      {KIND_LABEL[p.kind] || p.kind}
                    </span>
                    <button onClick={() => removePhoto(p)} className="absolute top-1 right-1 p-1 bg-red-500/80 text-white rounded opacity-0 group-hover:opacity-100">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {missingPhotos.length > 0 && (
              <div className="mt-6 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 text-sm">
                {missingPhotos.length} required photo{missingPhotos.length === 1 ? "" : "s"} missing: {missingPhotos.map((m) => m.label).join(", ")}.
              </div>
            )}
            {uploading && <div className="mt-3 text-white/50 text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</div>}
          </div>
        )}

        {/* STEP 4: DAMAGE */}
        {step === 4 && inspection && (
          <div data-testid="step-damage">
            <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
              {["top", "front", "rear", "driver", "passenger", "interior"].map((v) => (
                <button key={v} onClick={() => setDamageView(v)}
                  className={`px-4 py-2 rounded-lg text-sm capitalize font-medium transition-colors ${damageView === v ? "bg-[#1F5EFF] text-white" : "bg-white/5 border border-white/10 text-white/60 hover:text-white"}`}
                  data-testid={`damage-view-${v}`}>{v}</button>
              ))}
            </div>
            <CarDiagram view={damageView} markers={markers} onAdd={addMarker} onRemove={(m) => setMarkerDetail(m)} />
            <div className="mt-3 text-white/50 text-xs">
              Tap the vehicle where the damage is. {markers.length} marker{markers.length === 1 ? "" : "s"} recorded.
            </div>
          </div>
        )}

        {/* STEP 5: REVIEW — show the customer everything found before signing */}
        {step === 5 && inspection && (
          <div data-testid="step-review">
            <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">SHOW THE CUSTOMER BEFORE SIGNING</div>
              <p className="text-sm text-white/60 leading-relaxed">
                Everything found during the inspection. If something is missing, go Back and add it — the signature on the next step covers exactly this list.
              </p>
            </div>

            {markers.length === 0 ? (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-sm" data-testid="review-clean">
                No damage or remarks recorded — the vehicle checks out clean.
              </div>
            ) : (
              <div className="space-y-4" data-testid="review-markers">
                {["top", "front", "rear", "driver", "passenger", "interior"]
                  .map((v) => [v, markers.filter((m) => m.view === v)])
                  .filter(([, list]) => list.length > 0)
                  .map(([v, list]) => (
                    <div key={v} className="rounded-xl border border-white/10 overflow-hidden bg-black/20">
                      <div className="px-4 py-2 border-b border-white/10 font-mono-alt text-[10px] uppercase tracking-widest text-white/45">
                        {v} view — {list.length} marker{list.length === 1 ? "" : "s"}
                      </div>
                      <CarDiagram view={v} markers={markers} readonly />
                      <ul className="divide-y divide-white/5 bg-[#0a0a0d]">
                        {list.map((m, i) => {
                          const c = SEVC[m.severity] || SEVC.minor;
                          const typeLabel = (DAMAGE_TYPES.find((t) => t.key === m.type) || {}).label || m.type || "Damage";
                          return (
                            <li key={m.id} className="px-4 py-2.5 flex items-start gap-3" data-testid={`review-marker-${m.id}`}>
                              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 mt-0.5" style={{ background: c }}>
                                {i + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm text-white capitalize">{m.severity || "minor"} {typeLabel.toLowerCase()}</span>
                                {m.description && <div className="text-xs text-white/50 mt-0.5">{m.description}</div>}
                              </div>
                              {m.photo_ids?.length > 0 && (
                                <span className="text-[10px] text-[#8db2ff] whitespace-nowrap mt-1">
                                  {m.photo_ids.length} photo{m.photo_ids.length === 1 ? "" : "s"}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
              </div>
            )}

            {(info.mileage || info.fuel_level || info.warning_lights) && (
              <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10 text-sm space-y-1.5" data-testid="review-info">
                {info.mileage && <div className="text-white/60">Mileage: <span className="text-white">{Number(info.mileage).toLocaleString("en-CA")} km</span></div>}
                {info.fuel_level && <div className="text-white/60">Fuel: <span className="text-white capitalize">{info.fuel_level}</span></div>}
                {info.warning_lights && <div className="text-white/60">Warning lights: <span className="text-amber-300">{info.warning_lights}</span></div>}
              </div>
            )}
          </div>
        )}

        {/* STEP 6: SIGN */}
        {step === 6 && inspection && (
          <div data-testid="step-sign">
            <div className="mb-4 p-4 rounded-xl bg-white/5 border border-white/10 text-sm text-white/70 leading-relaxed">
              Customer acknowledges that the vehicle condition and existing damage documented in this inspection accurately represent the vehicle condition at the time it was received by WTD Detailing &amp; Tint.
            </div>
            <label className="block mb-4">
              <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">CUSTOMER NAME (PRINTED)</div>
              <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder={`${customer?.first_name || ""} ${customer?.last_name || ""}`.trim()} className={inp} data-testid="ci-signer-name" />
            </label>
            <SignaturePad onSave={finalize} />
          </div>
        )}
      </div>

      {/* Sticky bottom nav */}
      <div className="fixed bottom-0 inset-x-0 lg:left-72 bg-[#0a0a0d]/95 backdrop-blur border-t border-white/10 z-40">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-3 flex items-center gap-2">
          <button onClick={back} disabled={step === 0}
            className="px-4 py-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-30 flex items-center gap-1" data-testid="ci-back">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex-1" />
          {step < STEPS.length - 1 && (
            <button onClick={next} disabled={!canNext() || busy}
              className="px-6 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium disabled:opacity-40 flex items-center gap-2 min-w-[140px] justify-center" data-testid="ci-next">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Next <ChevronRight className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>

      {/* Marker type picker modal */}
      {markerBuf && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setMarkerBuf(null)}>
          <div className="w-full max-w-md bg-[#131319] border border-white/12 rounded-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="marker-picker">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-4 h-4 text-[#1F5EFF]" />
              <div className="text-white font-medium">Add damage marker</div>
            </div>
            <MarkerForm onSubmit={commitMarker} onCancel={() => setMarkerBuf(null)} />
          </div>
        </div>
      )}

      {/* Existing marker detail modal (photo + delete) */}
      {markerDetail && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setMarkerDetail(null)}>
          <div className="w-full max-w-sm bg-[#131319] border border-white/12 rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="marker-detail">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${markerDetail.severity === "major" ? "bg-red-500" : markerDetail.severity === "moderate" ? "bg-amber-500" : "bg-sky-400"}`} />
              <div className="text-white font-medium capitalize">
                {markerDetail.type} · {markerDetail.severity}
              </div>
            </div>
            {markerDetail.description && <p className="mt-2 text-sm text-white/60">{markerDetail.description}</p>}
            {markerDetail.photo_ids?.length > 0 && (
              <img src={`${BACKEND}/api/crm/inspections/${inspection.id}/photos/${markerDetail.photo_ids[0]}/file`}
                alt="Damage" className="mt-3 w-full max-h-72 object-contain rounded-xl border border-white/10 bg-black/40"
                data-testid="marker-detail-photo" />
            )}
            <div className="flex items-center gap-2 mt-4">
              <button onClick={async () => { await removeMarker(markerDetail); setMarkerDetail(null); }}
                className="flex-1 py-2.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 text-sm flex items-center justify-center gap-2"
                data-testid="marker-detail-delete">
                <Trash2 className="w-3.5 h-3.5" /> Remove marker
              </button>
              <button onClick={() => setMarkerDetail(null)} className="px-4 py-2.5 text-white/50 hover:text-white text-sm" data-testid="marker-detail-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkerForm({ onSubmit, onCancel }) {
  const [type, setType] = useState("scratch");
  const [severity, setSeverity] = useState("minor");
  const [desc, setDesc] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  return (
    <>
      <div className="text-[10px] font-mono-alt text-white/50 mb-2 uppercase tracking-widest">Type</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {DAMAGE_TYPES.map((t) => (
          <button key={t.key} onClick={() => setType(t.key)} data-testid={`marker-type-${t.key}`}
            className={`px-3 py-2 rounded-lg text-sm ${type === t.key ? "bg-[#1F5EFF] text-white" : "bg-white/5 border border-white/10 text-white/60"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="text-[10px] font-mono-alt text-white/50 mb-2 uppercase tracking-widest">Severity</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {["minor", "moderate", "major"].map((s) => (
          <button key={s} onClick={() => setSeverity(s)} data-testid={`marker-severity-${s}`}
            className={`px-3 py-2 rounded-lg text-sm capitalize ${severity === s ? (s === "major" ? "bg-red-500 text-white" : s === "moderate" ? "bg-amber-500 text-white" : "bg-[#1F5EFF] text-white") : "bg-white/5 border border-white/10 text-white/60"}`}>
            {s}
          </button>
        ))}
      </div>
      <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" rows={2} className={inp + " mb-3"} data-testid="marker-desc" />
      <div className="text-[10px] font-mono-alt text-white/50 mb-2 uppercase tracking-widest">Damage photo (optional)</div>
      {preview ? (
        <div className="relative mb-3 w-28">
          <img src={preview} alt="Damage preview" className="w-28 h-28 object-cover rounded-lg border border-white/12" data-testid="marker-photo-preview" />
          <button onClick={() => { setPhoto(null); setPreview(null); }}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#131319] border border-white/20 text-white/70 hover:text-white flex items-center justify-center"
            data-testid="marker-photo-clear">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <label className="mb-3 flex items-center gap-2 px-4 py-3 rounded-lg bg-white/5 border border-dashed border-white/15 hover:border-[#1F5EFF]/60 hover:text-white text-white/60 text-sm cursor-pointer transition-colors"
          data-testid="marker-photo-btn">
          <Camera className="w-4 h-4" /> Add photo of the damage
          <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="marker-photo-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPhoto(f); setPreview(URL.createObjectURL(f)); } e.target.value = ""; }} />
        </label>
      )}
      <div className="flex items-center gap-2">
        <button onClick={() => onSubmit({ type, severity, description: desc, photoFile: photo })} className="flex-1 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium" data-testid="marker-save">Save marker</button>
        <button onClick={onCancel} className="px-4 py-3 text-white/50 hover:text-white text-sm">Cancel</button>
      </div>
    </>
  );
}
